import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { isEmpty } from "lodash-es"
import { currentUserId as getCurrentUserId } from "reduxSlices/sessionSlice"
import { getOrganizationId } from "reduxSlices/currentSettingsSlice"
import {
  clearFormSubmissionCounts,
  clearFormSubmissions,
  getFormSubmissionTable,
  setCategories,
  setColumnOrder,
  setCurrentSort,
  setFormIdsToHide,
  setNoLocationAnswerOption,
  setDepartmentIdsToHide,
  setSelectedGrouping,
  setShow,
  setVisibleColumns,
  switchSolution,
} from "reduxSlices/formSubmissionTableSlice"
import { LocalStoragePersistence } from "utils/Persistence"

const CATEGORY_SLUG = "categorySlug"
const COLUMN_ORDER = "columnOrder"
const GROUP_BY = "groupBy"
const HIDDEN_FORM_IDS = "formIdsToHide"
const HIDDEN_DEPARTMENT_IDS = "departmentIdsToHide"
const NO_LOCATION_ANSWER_OPTION = "noLocationAnswerOption"
const SHOW = "show"
const SORT = "sort"
const VISIBLE_COLUMNS = "visibleColumns"

const useFormSubmissionsListSettings = ({
  categories,
  defaultCategorySlug,
  persistence = LocalStoragePersistence,
}) => {
  const dispatch = useDispatch()

  const currentUserId = useSelector(getCurrentUserId)
  const organizationId = useSelector(getOrganizationId)

  const {
    columnOrder,
    currentSort,
    departmentIdsToHide,
    formIdsToHide,
    groupBy,
    noLocationAnswerOption,
    selectedCategorySlug,
    show,
    visibleColumns,
  } = useSelector(getFormSubmissionTable)

  const oldKey = useMemo(() => (
    `form-submission-list-settings-${currentUserId}`
  ), [currentUserId])
  const key = useMemo(() => (
    `form-submission-index-settings-org-${organizationId}-user-${currentUserId}`
  ), [currentUserId, organizationId])

  const newKeyValues = persistence.getValue({ key })

  if (isEmpty(newKeyValues)) {
    persistence.copyAllValues({ keyToCopy: oldKey, keyToSet: key })

    persistence.clear({ key: oldKey })
  }

  // If these properties have been persisted,
  // we'll update their values in the store on mount
  const propertiesToLookupOnMount = [
    { property: COLUMN_ORDER, reducer: setColumnOrder },
    { property: GROUP_BY, reducer: setSelectedGrouping },
    { property: HIDDEN_FORM_IDS, reducer: setFormIdsToHide },
    { property: NO_LOCATION_ANSWER_OPTION, reducer: setNoLocationAnswerOption },
    { property: HIDDEN_DEPARTMENT_IDS, reducer: setDepartmentIdsToHide },
    { property: SHOW, reducer: setShow },
    { property: SORT, reducer: setCurrentSort },
    { property: VISIBLE_COLUMNS, reducer: setVisibleColumns },
  ]

  // These properties' values will be persisted
  const propertiesToPersist = [
    { property: CATEGORY_SLUG, value: selectedCategorySlug },
    { property: COLUMN_ORDER, value: columnOrder },
    { property: GROUP_BY, value: groupBy },
    { property: HIDDEN_FORM_IDS, value: formIdsToHide },
    { property: NO_LOCATION_ANSWER_OPTION, value: noLocationAnswerOption },
    { property: HIDDEN_DEPARTMENT_IDS, value: departmentIdsToHide },
    { property: SHOW, value: show },
    { property: SORT, value: currentSort },
    { property: VISIBLE_COLUMNS, value: visibleColumns },
  ]

  // Clear form submissions when component unmounts
  useEffect(() => (
    () => {
      dispatch(clearFormSubmissions())
      dispatch(clearFormSubmissionCounts())
    }
  ), [dispatch])

  useEffect(() => {
    // Always set categories
    dispatch(setCategories(categories))

    propertiesToLookupOnMount.forEach(({ property, reducer }) => {
      if (persistence.isPropertySet({ key, property })) {
        dispatch(reducer(persistence.lookup({ key, property })))
      }
    })

    if (persistence.isPropertySet({ key, property: SORT })) {
      dispatch(setCurrentSort(persistence.lookup({ key, property: SORT })))
    }

    // Always set a solution
    // Default to the user's previous settings, as stored in localStorage.
    // If no previous settings, fallback to the solution specified by the backend.
    // If the user does not have access to any form submissions in the solution
    // specified by the backend, fallback to the first solution to which
    // the user does have access to form submissions.
    let initialCategorySlug = persistence.lookup({ key, property: CATEGORY_SLUG })

    if (initialCategorySlug && !categories.some((category) => category.slug === initialCategorySlug)) {
      initialCategorySlug = null
    }

    if (!initialCategorySlug && !isEmpty(categories)) {
      const initialCategory = categories.reduce((targetCategory, currentCategory) => {
        if (currentCategory.slug === defaultCategorySlug) {
          targetCategory = currentCategory
        }

        return targetCategory
      })

      initialCategorySlug = initialCategory?.slug
    }

    const usedForSurveys = categories.some((category) => category.usedForSurvey && (category.slug === initialCategorySlug))

    dispatch(switchSolution({ categorySlug: initialCategorySlug, usedForSurveys }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Disabling some eslint rules because we're iterating over a hardcoded array
  propertiesToPersist.forEach(({ property, value }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      persistence.persist({ key, property, value })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, persistence, property, value])
  })

  return {}
}

export default useFormSubmissionsListSettings
